import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { X, Trash2, Eye, ChevronLeft, ChevronRight } from 'lucide-react';

const STORY_DURATION_MS = 5000;

const Avatar = ({ src, name, size = 32 }) =>
  src ? (
    <img
      src={src}
      alt={name || ''}
      className="rounded-full object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-semibold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );

const StoryViewer = ({
  token,
  API,
  t,
  isRTL,
  groups,           // [{user_id, user_name, profile_picture_url, stories:[...]}]
  startUserIndex,
  currentUserId,
  onClose,
  onAfterChange,    // called after a story or group is consumed (so carousel can refresh)
}) => {
  const [groupIdx, setGroupIdx] = useState(startUserIndex);
  const [storyIdx, setStoryIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [paused, setPaused] = useState(false);
  const tickRef = useRef(null);
  const startedAt = useRef(Date.now());
  const currentRef = useRef(null);

  const group = groups[groupIdx];
  const story = group?.stories?.[storyIdx];

  // Reset progress when story changes; mark viewed on display.
  useEffect(() => {
    setProgress(0);
    startedAt.current = Date.now();
    if (!story) return;
    if (story.user_id !== currentUserId && !story.viewed_by_me) {
      axios
        .post(`${API}/stories/${story.id}/view`, {}, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }
  }, [story, currentUserId, API, token]);

  const advance = useCallback(() => {
    onAfterChange && onAfterChange();
    if (groupIdx >= groups.length) return;
    if (storyIdx < (group?.stories?.length || 1) - 1) {
      setStoryIdx((i) => i + 1);
    } else if (groupIdx < groups.length - 1) {
      setGroupIdx((i) => i + 1);
      setStoryIdx(0);
    } else {
      onClose && onClose();
    }
  }, [groupIdx, storyIdx, group, groups.length, onAfterChange, onClose]);

  const back = useCallback(() => {
    if (storyIdx > 0) {
      setStoryIdx((i) => i - 1);
    } else if (groupIdx > 0) {
      const prev = groups[groupIdx - 1];
      setGroupIdx((i) => i - 1);
      setStoryIdx((prev?.stories?.length || 1) - 1);
    } else {
      setProgress(0);
      startedAt.current = Date.now();
    }
  }, [storyIdx, groupIdx, groups]);

  // Progress timer
  useEffect(() => {
    if (paused) return;
    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - startedAt.current;
      const p = Math.min(1, elapsed / STORY_DURATION_MS);
      setProgress(p);
      if (p >= 1) {
        clearInterval(tickRef.current);
        advance();
      }
    }, 80);
    return () => clearInterval(tickRef.current);
  }, [paused, advance, story?.id]);

  // Keyboard support
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose && onClose();
      else if (e.key === 'ArrowRight') (isRTL ? back : advance)();
      else if (e.key === 'ArrowLeft') (isRTL ? advance : back)();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [advance, back, onClose, isRTL]);

  // Pause on hold for the body region
  const onPointerDown = () => {
    setPaused(true);
    currentRef.current = Date.now() - startedAt.current;
  };
  const onPointerUp = () => {
    if (currentRef.current != null) {
      startedAt.current = Date.now() - currentRef.current;
    }
    setPaused(false);
  };

  const tapRegion = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    if (ratio < 0.33) (isRTL ? advance : back)();
    else if (ratio > 0.66) (isRTL ? back : advance)();
  };

  const deleteStory = async () => {
    if (!story) return;
    if (!window.confirm(t('confirmDeleteStory'))) return;
    try {
      await axios.delete(`${API}/stories/${story.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onAfterChange && onAfterChange();
      // Remove the story from local view; if group is empty, close.
      if ((group?.stories?.length || 0) <= 1) {
        onClose && onClose();
        return;
      }
      // splice in place by recomputing index
      group.stories.splice(storyIdx, 1);
      const newIdx = Math.min(storyIdx, group.stories.length - 1);
      setStoryIdx(newIdx);
      setProgress(0);
      startedAt.current = Date.now();
    } catch {
      alert(t('error'));
    }
  };

  if (!story) {
    onClose && onClose();
    return null;
  }

  const total = group.stories.length;
  const isOwn = story.user_id === currentUserId;

  return (
    <div
      data-testid="story-viewer"
      dir={isRTL ? 'rtl' : 'ltr'}
      role="dialog"
      aria-modal="true"
      aria-label={group.user_name || t('stories')}
      className="fixed inset-0 z-50 bg-black flex flex-col"
    >
      {/* Progress bars */}
      <div
        role="progressbar"
        aria-valuemin="1"
        aria-valuemax={total}
        aria-valuenow={storyIdx + 1}
        aria-label={`${storyIdx + 1} / ${total}`}
        className="flex gap-1 px-3 pt-3"
      >
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white"
              style={{
                width: i < storyIdx ? '100%' : i === storyIdx ? `${progress * 100}%` : '0%',
                transition: i === storyIdx ? 'none' : 'width 80ms linear',
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="flex items-center justify-between px-3 py-2 text-white">
        <div className="flex items-center gap-2 min-w-0">
          <Avatar src={group.profile_picture_url} name={group.user_name} size={32} />
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{group.user_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {isOwn && (
            <span
              data-testid="story-viewer-views"
              className="inline-flex items-center gap-1 text-xs text-white/80 bg-white/10 px-2 py-1 rounded-full"
            >
              <Eye className="w-3.5 h-3.5" />
              {story.views_count || 0}
            </span>
          )}
          {isOwn && (
            <button
              onClick={deleteStory}
              data-testid="story-viewer-delete"
              className="p-2 text-white/80 hover:text-white"
              aria-label={t('deleteStory')}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={onClose}
            data-testid="story-viewer-close"
            className="p-2 text-white/80 hover:text-white"
            aria-label={t('close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Body */}
      <div
        className="flex-1 relative select-none"
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onPointerCancel={onPointerUp}
        onClick={tapRegion}
      >
        <img
          src={story.media_url}
          alt=""
          className="absolute inset-0 w-full h-full object-contain"
        />
        {/* Hint chevrons (desktop) */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            back();
          }}
          data-testid="story-viewer-prev"
          className="hidden sm:flex absolute top-1/2 -translate-y-1/2 start-2 p-2 rounded-full bg-black/30 text-white hover:bg-black/50"
          aria-label={t('back')}
        >
          <ChevronLeft className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            advance();
          }}
          data-testid="story-viewer-next"
          className="hidden sm:flex absolute top-1/2 -translate-y-1/2 end-2 p-2 rounded-full bg-black/30 text-white hover:bg-black/50"
          aria-label="Next"
        >
          <ChevronRight className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} />
        </button>
      </div>
    </div>
  );
};

export default StoryViewer;
